import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from '@/axios'

export const usePlayerStore = defineStore('player', () => {
  // 基本字段
  const id = ref(null)
  const username = ref('')
  const playerName = ref('')
  const avatarUrl = ref('')
  const coins = ref(0)
  const diamonds = ref(0)       // ✅ 钻石
  const characterId = ref(1)
  const towerProgress = ref(1)
  const stamina = ref(1)        // 体力
  const pvpRank = ref(0)        // ✅ PVP 段位（数值）
  const isLoaded = ref(false)

  // ======================== 段位显示计算 ========================
  function formatPvpRank(nRaw) {
    const n = Math.max(0, Number(nRaw) || 0)
    if (n === 0) return '未定级'

    const ranges = [
      { name: '青铜',  start: 1,   end: 10 },
      { name: '白银',  start: 11,  end: 20 },
      { name: '黄金',  start: 21,  end: 50 },
      { name: '钻石',  start: 51,  end: 150 },
      { name: '星耀',  start: 151, end: 300 },
      { name: '王者',  start: 301, end: 500 },
    ]

    for (const r of ranges) {
      if (n >= r.start && n <= r.end) {
        const star = n - r.start + 1
        return `${r.name}${star}星`
      }
    }

    if (n >= 501) {
      const star = n - 500
      return `大皇帝${star}星`
    }

    return '未定级'
  }

  const pvpRankLabel = computed(() => formatPvpRank(pvpRank.value))

  // ======================== 拉取玩家信息 ========================
  async function fetchPlayerInfo(userId) {
    try {
      const { data } = await axios.get(`/user/info/${userId}`)
      id.value = data.id
      username.value = data.username
      playerName.value = data.player_name
      avatarUrl.value = `${data.avatarUrl}?t=${Date.now()}`
      coins.value = data.coins
      diamonds.value = data.diamonds ?? 0   // ✅ 拉取钻石
      characterId.value = data.character_id
      towerProgress.value = data.tower_progress
      stamina.value = data.stamina
      pvpRank.value = data.pvp_rank ?? 0
      isLoaded.value = true
    } catch (e) {
      isLoaded.value = false
      console.error('获取玩家信息失败', e)
    }
  }

  // ✅ 单独拉取 PVP 段位
  async function fetchPvpRank() {
    if (!id.value) return
    try {
      const { data } = await axios.get(`/pvp/rank/${id.value}`)
      pvpRank.value = Number(data?.pvp_rank ?? 0)
    } catch (e) {
      console.error('获取 PVP 段位失败', e)
    }
  }

  // ======================== 体力与金币/钻石 ========================
  function setStamina(newVal) {
    stamina.value = newVal
  }

  async function addStamina(amount) {
    if (!id.value) throw new Error('userId is not loaded')
    await axios.post('/stamina/add', { userId: id.value, amount })
    await fetchPlayerInfo(id.value)
  }

  async function deductStamina(amount) {
    if (!id.value) throw new Error('userId is not loaded')
    await axios.post('/stamina/deduct', { userId: id.value, amount })
    await fetchPlayerInfo(id.value)
  }

  function setCoins(newVal) {
    coins.value = newVal
  }

  async function addCoins(amount) {
    if (!id.value) throw new Error('userId is not loaded')
    await axios.post('/coin/add', { userId: id.value, amount })
    await fetchPlayerInfo(id.value)
  }

  async function deductCoins(amount) {
    if (!id.value) throw new Error('userId is not loaded')
    await axios.post('/coin/deduct', { userId: id.value, amount })
    await fetchPlayerInfo(id.value)
  }

  // ✅ 钻石三件套
  function setDiamonds(newVal) {
    diamonds.value = newVal
  }

  async function addDiamonds(amount) {
    if (!id.value) throw new Error('userId is not loaded')
    await axios.post('/diamond/add', { userId: id.value, amount })
    await fetchPlayerInfo(id.value)
  }

  async function deductDiamonds(amount) {
    if (!id.value) throw new Error('userId is not loaded')
    await axios.post('/diamond/deduct', { userId: id.value, amount })
    await fetchPlayerInfo(id.value)
  }

  // ======================== 通用更新 ========================
  async function updatePlayerInfo(updateObj = {}) {
    if (!id.value) throw new Error('userId is not loaded')
    const payload = { userId: id.value, ...updateObj }
    try {
      await axios.post('/user/update', payload)
      await fetchPlayerInfo(id.value)
      return true
    } catch (e) {
      console.error('更新玩家信息失败', e)
      return false
    }
  }

  return {
    // state
    id,
    username,
    playerName,
    avatarUrl,
    coins,
    diamonds,            // ✅ 导出钻石
    characterId,
    towerProgress,
    stamina,
    pvpRank,
    pvpRankLabel,
    isLoaded,

    // actions
    fetchPlayerInfo,
    fetchPvpRank,
    updatePlayerInfo,

    setCoins,
    addCoins,
    deductCoins,

    setDiamonds,         // ✅ 导出方法
    addDiamonds,
    deductDiamonds,

    setStamina,
    addStamina,
    deductStamina
  }
})
